/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DATA VALIDATION SCHEMAS (PRODUCTION READY - SUPABASE COMPATIBLE)
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { z } from 'zod';
import { toCents } from '../core/utils.js';

// ══════════════════════════════════════════════════════════════════════════════
// BASE SCHEMAS (REUSABLE COMPONENTS)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * ISO 8601 date string (YYYY-MM-DD)
 */
const isoDateString = z.string().regex(
  /^\d{4}-\d{2}-\d{2}$/,
  'Date must be in YYYY-MM-DD format'
);

/**
 * ISO 8601 datetime string
 */
const isoDateTimeString = z.string().datetime({
  message: 'Must be valid ISO datetime string'
});

/**
 * Non-negative number (0 or positive)
 */
const nonNegativeNumber = z.number().nonnegative({
  message: 'Must be zero or positive'
});

/**
 * Positive number (greater than 0)
 */
const positiveNumber = z.number().positive({
  message: 'Must be greater than zero'
});

/**
 * Non-empty string
 */
const nonEmptyString = z.string().min(1, {
  message: 'Cannot be empty'
});

/**
 * ID pattern (generated IDs like "inv_123_abc")
 */
const idPattern = z.string().regex(
  /^[a-z]+_\d+_[a-z0-9]+$/,
  'Invalid ID format'
);

/**
 * Optional string (can be empty, null, or undefined - defaults to empty string)
 */
const optionalString = z.string().optional().default('');

// ══════════════════════════════════════════════════════════════════════════════
// MONEY SCHEMAS (FLOATING-POINT FIX)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Money value in cents (INTEGER)
 * Accepts both string ("10.50") and number (10.50) input
 * Stores as integer cents (1050)
 * 
 * Max: 100,000,000.00 (10 billion cents = $100 million)
 */
const moneyCents = z.union([
  z.string().regex(/^\d+(\.\d{1,2})?$/, 'Invalid money format'),
  z.number()
])
.transform(val => toCents(val))
.pipe(
  z.number()
    .int('Money must be stored as integer cents')
    .nonnegative('Money cannot be negative')
    .max(10000000000, 'Amount too large (max $100,000,000)')
);

/**
 * Money value that must be positive (> 0)
 * Used for invoice totals, product prices
 */
const positiveMoneyCents = z.union([
  z.string().regex(/^\d+(\.\d{1,2})?$/, 'Invalid money format'),
  z.number()
])
.transform(val => toCents(val))
.pipe(
  z.number()
    .int('Money must be stored as integer cents')
    .positive('Amount must be greater than zero')
    .max(10000000000, 'Amount too large (max $100,000,000)')
);

/**
 * Money value that can be negative (for refunds)
 * Used for payment amounts (refunds are negative)
 */
const signedMoneyCents = z.union([
  z.string().regex(/^-?\d+(\.\d{1,2})?$/, 'Invalid money format'),
  z.number()
])
.transform(val => toCents(val))
.pipe(
  z.number()
    .int('Money must be stored as integer cents')
    .min(-10000000000, 'Amount too large (max $100,000,000)')
    .max(10000000000, 'Amount too large (max $100,000,000)')
);

/**
 * Percentage value (0-100)
 * No conversion needed - percentages don't have precision issues
 */
const percentage = z.number()
  .nonnegative('Percentage cannot be negative')
  .max(100, 'Percentage cannot exceed 100%');

/**
 * Quantity (allows decimals, but stored as number for now)
 */
const quantity = z.number()
  .positive('Quantity must be greater than zero')
  .max(1000000, 'Quantity too large');

// ══════════════════════════════════════════════════════════════════════════════
// CUSTOMER SCHEMA
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Customer object (embedded in invoices & standalone customer records)
 * MaxLength constraints match UI form limits AND database constraints
 * 
 * ✅ ZOD V4 COMPATIBLE - Proper email validation with union pattern
 * ✅ SUPABASE COMPATIBLE - Matches database field names
 */
export const customerSchema = z.object({
  // Required field
  name: nonEmptyString.max(45, 'Name too long (max 45 characters)'),
  
  // Contact info (optional)
  phone: z.string().max(20, 'Phone too long (max 20 characters)').default(''),
  
  // ✅ FIXED: Email validation with max length inside union
  email: z.union([
    z.string().email('Invalid email address').max(50, 'Email too long (max 50 characters)'),
    z.literal('')
  ]).default(''),
  
  gstin: z.string().max(20, 'Tax ID too long (max 20 characters)').default(''),
 
  // Address (all optional)
  addressLine1: z.string().max(100, 'Address line 1 too long (max 100 characters)').default(''),
  addressLine2: z.string().max(100, 'Address line 2 too long (max 100 characters)').default(''),
  city: z.string().max(85, 'City too long (max 85 characters)').default(''),
  state: z.string().max(50, 'State too long (max 50 characters)').default(''),
  country: z.string().max(50, 'Country too long (max 50 characters)').default(''),
  zip: z.string().max(12, 'ZIP too long (max 12 characters)').default('')
}).strict();

// ══════════════════════════════════════════════════════════════════════════════
// DISCOUNT SCHEMA (FIXED FOR CENTS)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Discount object (percentage or fixed amount)
 * 
 * If type="pct": value is percentage (0-100)
 * If type="amt": value is in CENTS
 */
export const discountSchema = z.object({
  type: z.enum(['pct', 'amt'], {
    errorMap: () => ({ message: 'Discount type must be "pct" or "amt"' })
  }),
  value: z.union([
    z.string(),
    z.number()
  ]).transform(val => {
    if (val === '' || val === null || val === undefined) {
      return 0;
    }
    return typeof val === 'number' ? val : parseFloat(val) || 0;
  })
}).strict();

// ══════════════════════════════════════════════════════════════════════════════
// INVOICE LINE ITEM SCHEMA (FIXED FOR CENTS)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Tax split breakdown (all in CENTS)
 */
const taxSplitSchema = z.object({
  igst: moneyCents,
  sgst: moneyCents,
  cgst: moneyCents,
  other: moneyCents,
  other2: moneyCents
}).strict();

/**
 * Invoice line item (all money values in CENTS)
 */
export const invoiceLineSchema = z.object({
  // Product identification
  name: nonEmptyString.max(500, 'Product name too long'),
  productId: z.string().max(100, 'Product ID too long').default(''),
  hsn: z.string().max(50, 'HSN too long').default(''),
  barcode: z.string().max(100, 'Barcode too long').default(''),
  unit: z.string().max(50, 'Unit too long').default(''),
 
  // Quantities and pricing
  qty: quantity,
  price: moneyCents,
  base: moneyCents,
 
  // Tax rates (percentages)
  tax: percentage,
  tax2: percentage,
 
  // Discounts
  itemDiscount: discountSchema,
  discAmt: moneyCents,
  afterDisc: moneyCents,
 
  // Tax amounts
  taxTotal: moneyCents,
  sgstAmt: moneyCents,
  cgstAmt: moneyCents,
  igstAmt: moneyCents,
  otherAmt: moneyCents,
  other2Amt: moneyCents,
  split: taxSplitSchema,
 
  // Total
  total: moneyCents,
 
  // Custom fields
  customField1Value: z.string().max(500, 'Custom field 1 too long').default(''),
  customField2Value: z.string().max(500, 'Custom field 2 too long').default('')
}).strict().refine(
  (data) => data.total >= 0,
  { message: 'Line total must be non-negative' }
);

// ══════════════════════════════════════════════════════════════════════════════
// INVOICE SCHEMA (FIXED FOR CENTS)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Complete invoice data (for creation)
 * All money values in CENTS
 */
export const invoiceSchema = z.object({
  // Version tracking (for optimistic locking)
  version: z.number().int().nonnegative().default(1),
  
  // Invoice identification
  invDate: isoDateString,
 
  // Edit tracking (invoice replacement chain)
  replacesInvoiceId: z.string().optional().nullable(),
  replacesInvoiceNo: z.string().optional().nullable(),
  replacedByInvoiceId: z.string().optional().nullable(),
  replacedByInvoiceNo: z.string().optional().nullable(),
 
  // Customer (snapshot)
  customer: customerSchema,
 
  // Transaction type (for GST India)
  txType: z.string().max(50).optional().nullable(),
 
  // Line items (MUST have at least one)
  lines: z.array(invoiceLineSchema).min(1, {
    message: 'Invoice must have at least one line item'
  }).max(1000, {
    message: 'Too many line items (max 1000)'
  }),
 
  // Totals (all in CENTS)
  subtotal: moneyCents,
  totalTax: moneyCents,
  itemDiscountTotal: moneyCents,
  overallDiscAmt: moneyCents,
  overallDiscount: discountSchema,
  grandTotal: positiveMoneyCents,
 
  // Notes
  notes: z.string().max(5000, 'Notes too long (max 5000 characters)').default('')
}).strict().refine(
  (data) => {
    // Business rule: grandTotal should equal calculated total
    const calculated = data.subtotal - data.itemDiscountTotal - data.overallDiscAmt + data.totalTax;
    const tolerance = 1; // 1 cent tolerance
    return Math.abs(data.grandTotal - calculated) <= tolerance;
  },
  {
    message: 'Grand total does not match calculated value (subtotal - discounts + taxes)',
    path: ['grandTotal']
  }
).refine(
  (data) => {
    // Business rule: at least one line item must have qty > 0
    return data.lines.some(line => line.qty > 0);
  },
  {
    message: 'At least one line item must have quantity greater than zero',
    path: ['lines']
  }
);

// ══════════════════════════════════════════════════════════════════════════════
// PAYMENT SCHEMA (FIXED FOR CENTS)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Payment data (for adding payment to invoice)
 * Amount in CENTS (can be negative for refunds)
 */
export const paymentSchema = z.object({
  amount: signedMoneyCents,
  paidAt: z.string().optional(),
  paymentMode: z.string().max(100, 'Payment mode too long').default(''),
  paymentDetails: z.string().max(1000, 'Payment details too long').default(''),
  recordedBy: z.string().max(100).default(''),
  isRefund: z.boolean().optional()
}).strict();

/**
 * Refund-specific schema (stricter validation)
 * Amount must be positive (user enters positive, we convert to negative)
 */
export const refundSchema = z.object({
  amount: positiveMoneyCents,
  paymentMode: z.string().min(1, 'Refund mode is required').max(100),
  paymentDetails: z.string().max(1000).default(''),
  paidAt: z.string().optional()
}).strict();

/**
 * Payment validation with invoice context
 * Amount in CENTS
 */
export const validatePaymentAmount = (amountCents, invoiceGrandTotalCents, alreadyPaidCents) => {
  const remainingCents = invoiceGrandTotalCents - alreadyPaidCents;
 
  if (amountCents > remainingCents + 1) {
    throw new Error(
      `Payment amount ($${(amountCents / 100).toFixed(2)}) exceeds remaining balance ($${(remainingCents / 100).toFixed(2)})`
    );
  }
 
  return true;
};

// ══════════════════════════════════════════════════════════════════════════════
// PRODUCT SCHEMA (FIXED FOR CENTS)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Product data (for create/update)
 * Price in CENTS
 */
export const productSchema = z.object({
  name: nonEmptyString.max(500, 'Product name too long'),
  price: moneyCents, // ✅ Converts dollars to cents
  tax: percentage.default(0), // ✅ ADDED: Default to 0
  tax2: percentage.default(0), // ✅ ADDED: Default to 0
  barcode: z.string().max(100, 'Barcode too long').default(''),
  productId: z.string().max(100, 'Product ID too long').default(''),
  hsn: z.string().max(50, 'HSN too long').default(''),
  unit: z.string().max(50, 'Unit too long').default(''),
  customField1Value: z.string().max(500, 'Custom field 1 too long').default(''),
  customField2Value: z.string().max(500, 'Custom field 2 too long').default(''),
  category: z.enum(['Product', 'Service', 'Subscription'], {
    errorMap: () => ({ message: 'Category must be Product, Service, or Subscription' })
  }).default('Product')
}).strict();

/**
 * Partial product schema (for updates)
 */
export const productUpdateSchema = productSchema.partial();

// ══════════════════════════════════════════════════════════════════════════════
// BRAND SNAPSHOT SCHEMA
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Brand snapshot data
 */
export const brandSnapshotSchema = z.object({
  // Business info
  name: z.string().max(200, 'Business name too long').default(''),
  owner: z.string().max(200, 'Owner name too long').default(''),
  phone: z.string().max(50, 'Phone too long').default(''),
  
  // ✅ FIXED: Email validation with max length inside union
  email: z.union([
    z.string().email('Invalid email address').max(200, 'Email too long (max 200 characters)'),
    z.literal('')
  ]).default(''),
  
  tagline: z.string().max(500, 'Tagline too long').default(''),
 
  // Address
  addressLine1: z.string().max(200).default(''),
  addressLine2: z.string().max(200).default(''),
  city: z.string().max(100).default(''),
  state: z.string().max(100).default(''),
  country: z.string().max(100).default(''),
  zip: z.string().max(20).default(''),
 
  // Tax info
  taxSystem: z.string().max(50).default('NONE'),
  taxId: z.string().max(100).default(''),
  canadaMode: z.string().max(50).default('GST_PST'),
  customTaxes: z.array(z.any()).default([]),
 
  // Image IDs (references to images table)
  logoId: z.string().nullable().optional(),
  signatureId: z.string().nullable().optional(),
  watermarkType: z.string().max(50).default('none'),
  watermarkText: z.string().max(200).default(''),
  watermarkImageId: z.string().nullable().optional()
}).strict();

// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS SNAPSHOT SCHEMA
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Custom field configuration
 */
const customFieldSchema = z.object({
  enabled: z.boolean(),
  name: z.string().max(100, 'Custom field name too long'),
  showInInvoice: z.boolean()
}).strict();

/**
 * Notes section configuration
 */
const notesSectionSchema = z.object({
  id: z.string(),
  title: z.string().max(200),
  content: z.string().max(5000),
  enabled: z.boolean()
}).strict();

/**
 * Settings snapshot data
 */
export const settingsSnapshotSchema = z.object({
  // Currency (from brand)
  currency: z.string().max(50).optional(),
  currencySymbol: z.string().max(10).optional(),
  customCurrencySymbol: z.string().max(10).optional(),
  currencyFormat: z.string().max(50).optional(),
 
  // Amount formatting
  amountGrouping: z.string().max(50).optional(),
  amountSeparator: z.string().max(50).optional(),
  amountDecimals: z.boolean().optional(),
 
  // Date & Time
  dateFormat: z.string().max(50).optional(),
  dateSeparator: z.string().max(10).optional(),
  timeFormat: z.string().max(50).optional(),
 
  // Page
  pageSize: z.string().max(50).optional(),
 
  // Workflow
  billingLayout: z.string().max(50).optional(),
  customerCardCollapsed: z.boolean().optional(),
 
  // Display options
  showHSN: z.boolean().optional(),
  showProductID: z.boolean().optional(),
  showDiscount: z.boolean().optional(),
 
  // Custom fields
  customField1: customFieldSchema.optional(),
  customField2: customFieldSchema.optional(),
 
  // Invoice numbering
  invPrefix: z.string().max(50).optional(),
  invDateFormat: z.string().max(50).optional(),
  invSeparator: z.string().max(10).optional(),
  invMinDigits: z.number().int().min(1).max(10).optional(),
 
  // Notes
  notesSections: z.array(notesSectionSchema).optional()
}).strict();

// ══════════════════════════════════════════════════════════════════════════════
// VALIDATION HELPER FUNCTIONS (FIXED FOR ZOD V4)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Validate data and return clean result
 * ✅ FIXED: Zod v4 uses .issues instead of .errors
 */
export function validate(schema, data) {
  const result = schema.safeParse(data);
 
  if (result.success) {
    return {
      success: true,
      data: result.data
    };
  }

  // ✅ ZOD V4: Use .issues instead of .errors
  const issues = result.error?.issues || [];
  
  // Format issues as errors for consistent API
  const errors = issues.map(issue => ({
    path: Array.isArray(issue.path) ? issue.path.join('.') : String(issue.path || ''),
    message: issue.message || 'Validation error',
    code: issue.code || 'unknown'
  }));
 
  return {
    success: false,
    errors,
    message: formatValidationErrors(errors)
  };
}

/**
 * Validate data and throw on error
 */
export function validateOrThrow(schema, data) {
  const result = validate(schema, data);
 
  if (!result.success) {
    const error = new Error(result.message);
    error.name = 'ValidationError';
    error.errors = result.errors;
    throw error;
  }
 
  return result.data;
}

/**
 * Format validation errors into readable message
 */
function formatValidationErrors(errors) {
  if (!errors || errors.length === 0) {
    return 'Validation failed';
  }
  
  if (errors.length === 1) {
    return `Validation error: ${errors[0].path ? errors[0].path + ': ' : ''}${errors[0].message}`;
  }
 
  const errorList = errors
    .map(err => `  • ${err.path ? err.path + ': ' : ''}${err.message}`)
    .join('\n');
 
  return `Validation failed with ${errors.length} error(s):\n${errorList}`;
}

/**
 * Create custom validation error
 */
export function createValidationError(message, path = '') {
  const error = new Error(message);
  error.name = 'ValidationError';
  error.errors = [{ path, message, code: 'custom' }];
  return error;
}

// ══════════════════════════════════════════════════════════════════════════════
// STORED FORMAT VALIDATORS (FOR IMPORT/BACKUP)
// ══════════════════════════════════════════════════════════════════════════════

export const storedInvoiceValidator = z.object({
  id: z.string(),
  invNo: z.string(),
  counter: z.number().int().nonnegative(),
  invDate: isoDateString,
  timestamp: isoDateTimeString,
  version: z.number().int().nonnegative().optional(),
  subtotal: z.number().int().nonnegative(),
  totalTax: z.number().int().nonnegative(),
  grandTotal: z.number().int().positive(),
  customer: z.object({
    name: z.string().min(1)
  }).passthrough(),
  status: z.enum(['active', 'void', 'deleted']).optional()
}).passthrough();

export const storedPaymentValidator = z.object({
  id: z.string(),
  invoiceId: z.string(),
  amount: z.number().int().positive(),
  paidAt: z.string(),
  deleted: z.boolean().optional()
}).passthrough();

export const storedProductValidator = z.object({
  id: z.string(),
  name: z.string().min(1),
  price: z.number().int().nonnegative(),
  tax: z.number().min(0).max(100),
  archived: z.boolean().optional()
}).passthrough();

export const storedCounterValidator = z.object({
  dateKey: z.string(),
  count: z.number().int().nonnegative(),
  version: z.number().int().nonnegative().optional()
}).passthrough();

// ══════════════════════════════════════════════════════════════════════════════
// DELETION SCHEMAS (NEW - RIGHT TO BE FORGOTTEN)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Batch restoration request validation
 * Ensures batchId is a valid UUID
 */
export const batchRestoreSchema = z.object({
  batchId: z.string().uuid('Invalid batch ID format')
}).strict();

/**
 * Deletion confirmation validation
 * Requires typing exact confirmation text + specifying data type
 * 
 * USAGE:
 * ```javascript
 * const result = validate(deletionConfirmationSchema, {
 *   confirmationText: 'DELETE',
 *   dataType: 'products'
 * });
 * ```
 */
export const deletionConfirmationSchema = z.object({
  confirmationText: z.string().refine(
    (val) => val === 'DELETE',
    { message: 'Must type DELETE to confirm' }
  ),
  dataType: z.enum(['products', 'customers', 'invoices'], {
    errorMap: () => ({ message: 'Invalid data type' })
  })
}).strict();


// ══════════════════════════════════════════════════════════════════════════════
// SUBSCRIPTION SCHEMAS (NEW - PHASE 2A)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Tier ID validation
 * Lowercase IDs for database consistency
 */
export const tierIdSchema = z.enum(['basic', 'professional', 'exclusive', 'enterprise'], {
  errorMap: () => ({ message: 'Invalid tier. Must be basic, professional, exclusive, or enterprise.' })
});

/**
 * Subscription tier structure validation
 * Used for admin operations and tier management
 */
export const subscriptionTierSchema = z.object({
  tier_id: tierIdSchema,
  tier_name: z.string().min(1, 'Tier name required'),
  tier_level: z.number().int().nonnegative(),
  monthly_price: z.number().int().nonnegative(), // in cents
  features: z.record(z.union([z.boolean(), z.number(), z.string(), z.null()])), // Flexible JSONB
  description: z.string().optional(),
  is_active: z.boolean().default(true)
}).strict();

/**
 * User subscription status validation
 */
export const subscriptionStatusSchema = z.enum(['active', 'inactive', 'expired', 'cancelled', 'trial'], {
  errorMap: () => ({ message: 'Invalid subscription status' })
});

/**
 * User subscription data validation
 */
export const userSubscriptionSchema = z.object({
  user_id: z.string().uuid(),
  tier_id: tierIdSchema,
  status: subscriptionStatusSchema,
  current_period_start: z.string().datetime().optional().nullable(),
  current_period_end: z.string().datetime().optional().nullable(),
  trial_end: z.string().datetime().optional().nullable(),
  cancelled_at: z.string().datetime().optional().nullable(),
  payment_gateway: z.string().optional().nullable(),
  gateway_subscription_id: z.string().optional().nullable(),
  gateway_customer_id: z.string().optional().nullable()
}).strict();

/**
 * Usage check result validation
 * Returned by canCreateInvoice/Product/Customer functions
 */
export const usageCheckSchema = z.object({
  allowed: z.boolean(),
  current: z.number().int().nonnegative().optional(),
  limit: z.number().int().nonnegative().nullable().optional(),
  remaining: z.number().int().nonnegative().nullable().optional(),
  unlimited: z.boolean().optional(),
  billing_period_end: z.string().optional(),
  reason: z.string().optional(),
  message: z.string().optional(),
  error: z.string().optional()
});

/**
 * Current usage stats validation
 */
export const usageStatsSchema = z.object({
  invoices_this_month: z.number().int().nonnegative(),
  invoice_limit: z.number().int().nonnegative().nullable(),
  products_total: z.number().int().nonnegative(),
  product_limit: z.number().int().nonnegative().nullable(),
  customers_total: z.number().int().nonnegative(),
  customer_limit: z.number().int().nonnegative().nullable()
});

/**
 * Complete subscription info validation
 * Returned by getUserSubscription()
 */
export const subscriptionInfoSchema = z.object({
  tier_id: tierIdSchema,
  tier_name: z.string(),
  tier_level: z.number().int().nonnegative(),
  monthly_price: z.number().int().nonnegative(),
  features: z.record(z.union([z.boolean(), z.number(), z.string(), z.null()])),
  status: subscriptionStatusSchema,
  current_period_start: z.string().datetime().nullable(),
  current_period_end: z.string().datetime().nullable(),
  billing_period_end: z.string().nullable(),
  usage: usageStatsSchema
});

/**
 * Subscription history entry validation
 */
export const subscriptionHistorySchema = z.object({
  user_id: z.string().uuid(),
  tier_id: tierIdSchema,
  action: z.enum(['created', 'upgraded', 'downgraded', 'renewed', 'cancelled', 'expired', 'reactivated', 'deleted']),
  amount_paid: z.number().int().nonnegative().optional().nullable(),
  payment_method: z.string().optional().nullable(),
  gateway_invoice_id: z.string().optional().nullable(),
  gateway_payment_id: z.string().optional().nullable(),
  period_start: z.string().datetime().optional().nullable(),
  period_end: z.string().datetime().optional().nullable(),
  notes: z.string().optional().nullable()
});

/**
 * Add to exports comment at the top of file
 */

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY
// ══════════════════════════════════════════════════════════════════════════════

/**
 * EXPORTED SCHEMAS (PRODUCTION READY):
 *
 * ✅ Zod v4 compatible - No z.literal('') patterns
 * ✅ Supabase compatible - Matches database schema
 * ✅ Money values in CENTS - Prevents floating-point errors
 * ✅ Field length constraints match UI and database
 * ✅ Comprehensive validation with clear error messages
 *
 * Core Entities:
 * - customerSchema - Customer information
 * - productSchema - Product data (MONEY IN CENTS)
 * - productUpdateSchema - Partial product updates
 * - invoiceSchema - Complete invoice validation (MONEY IN CENTS)
 * - invoiceLineSchema - Invoice line item (MONEY IN CENTS)
 * - paymentSchema - Payment data (MONEY IN CENTS)
 * - refundSchema - Refund-specific validation
 * - brandSnapshotSchema - Brand snapshot
 * - settingsSnapshotSchema - Settings snapshot
 *
 * Components:
 * - discountSchema - Discount configuration
 *
 * Stored Format Validators (for import/backup):
 * - storedInvoiceValidator
 * - storedPaymentValidator
 * - storedProductValidator
 * - storedCounterValidator
 *
 * Helpers:
 * - validate(schema, data) - Safe validation
 * - validateOrThrow(schema, data) - Validation with exception
 * - createValidationError(message, path) - Custom error
 * - validatePaymentAmount(amount, total, paid) - Payment business rule
 * 
 *  * ✅ VALIDATION:
 * - Schemas (invoiceSchema, paymentSchema, productSchema, etc.)
 * - Deletion Schemas (NEW):
 *   • batchRestoreSchema - Validate batch restoration
 *   • deletionConfirmationSchema - Validate deletion confirmation
 * - Helpers (validate, validateOrThrow, createValidationError)
 * 
 *  * ✅ SUBSCRIPTION (NEW - Phase 2A):
 * - Tier Schemas:
 *   • tierIdSchema - Validates tier ID (basic, professional, exclusive, enterprise)
 *   • subscriptionTierSchema - Complete tier structure
 *   • subscriptionStatusSchema - Status validation (active, inactive, expired, etc.)
 *   • userSubscriptionSchema - User subscription data
 * - Usage Schemas:
 *   • usageCheckSchema - Result from canCreate* functions
 *   • usageStatsSchema - Usage statistics structure
 *   • subscriptionInfoSchema - Complete subscription info from getUserSubscription()
 *   • subscriptionHistorySchema - Subscription history entry
 * 
 */